import React, { ReactElement } from 'react';
import { useParams } from 'react-router';
import { Link, LoaderButton } from 'idsk-react-components';
import { useSuccessMessageContext } from '../../../context/SuccessMessageContext';
import { useClient } from '../../../context/ClientStatusContext';
import { addPodporovatel, removePodporovatel } from '../../../client/podporovatelClient';
import TypPripomienky from '../../../model/pripomienka/TypPripomienky';
import PripomienkaDetail from '../../../model/pripomienka/PripomienkaDetail';
import './actionButtons.scss';
import ExportPodporovatelovButton from './ExportPodporovatelovButton';
import AssignMePripomienkaButton from '../../AssignMePripomienkaButton';
import { useUserContext } from '../../../context/UserContext';
import PripomienkaDetailRow from './PripomienkaDetailRow';
import UserRole from '../../../model/currentuser/UserRole';
import routes from '../../../routing/routes';
import Secured from '../../Secured';
import RemovePripomienkaButton from '../../RemovePripomienkaButton';
import ClassType from '../../../model/pripomienka/ClassType';
import { canEdit, processInCorrectStates, processIsInMpk } from '../../../function/permissions';
import StadiumMaterialuEnum from '../../../model/currentuser/StadiumMaterialuEnum';

interface Props {
  classType: ClassType;
  pripomienkaDetail: PripomienkaDetail,
  reloadPripomienkaDetail(): void;
  reloadFilterData?(): void;
}

const ActionButtons = ({
  pripomienkaDetail,
  reloadPripomienkaDetail,
  reloadFilterData,
  classType,
}: Props): ReactElement => {
  const {
    type,
    year,
    index,
    uuid,
    // @ts-ignore
  } = useParams<{ type: string, year: number, index: number, uuid: string }>();
  const { displaySuccess } = useSuccessMessageContext();
  const { wrapClient: addPodporovatelClient, status: addStatus } = useClient(addPodporovatel);
  const {
    wrapClient: removePodporovatelClient,
    status: removeStatus,
  } = useClient(removePodporovatel);
  const { pouzivatel, legislationProcess } = useUserContext();
  const onAddPodporovatelButtonClick = async (): Promise<void> => {
    await addPodporovatelClient(type, year, index, pripomienkaDetail.uuid);
    reloadPripomienkaDetail();
    displaySuccess('Podpora pripomienky bola úspešne zaznamenaná.');
  };

  const onRemovePodporovatelButtonClick = async (): Promise<void> => {
    await removePodporovatelClient(type, year, index, pripomienkaDetail.uuid);
    reloadPripomienkaDetail();
    displaySuccess('Podpora pripomienky bola zrušená.');
  };

  const getVyhodnotUrl = (): string => (pripomienkaDetail.typ === TypPripomienky.HROMADNA
    ? routes.hromadnePripomienky.evaluate.withParams(type, year, index, pripomienkaDetail.uuid)
    : routes.pripomienky.evaluate.withParams(type, year, index, pripomienkaDetail.uuid));

  const getEditUrl = (): string => (routes.pripomienky.edit.withParams(type, year, index, pripomienkaDetail.uuid));

  const getEditAdminUrl = (): string => (classType === ClassType.HROMADNA_PRIPOMIENKA
    ? routes.admin.hromadnePripomienky.edit.withParams(type, year, index, uuid)
    : routes.admin.pripomienky.edit.withParams(type, year, index, uuid));

  const podporaButton = (): ReactElement => {
    if (pouzivatel == null
      && pripomienkaDetail.typ === TypPripomienky.HROMADNA
      && legislationProcess.stadiumProcesu === StadiumMaterialuEnum.MPK) {
      return (
        <div className="unlogged-user-podpora-text">
          <PripomienkaDetailRow
            header=""
            data="Ak chcete podporiť hromadnú pripomienku, prihláste sa."
          />
        </div>
      );
    }
    if (pripomienkaDetail.somPodporovatel) {
      return (
        <Secured
          allowForRoles={[UserRole.NELEGISLATIVNA_ORGANIZACIA, UserRole.VEREJNOST, UserRole.PRIPOMIENKOVATEL,
            UserRole.VYHODNOCOVATEL]}
          additionalCheck={processIsInMpk(legislationProcess) && pripomienkaDetail.typ === TypPripomienky.HROMADNA}
        >
          <LoaderButton
            onClick={onRemovePodporovatelButtonClick}
            loading={removeStatus.loading}
            type="button"
            className="remove-support-button"
            buttonStyle="warning"
            size="large"
          >Zrušiť podporu
          </LoaderButton>
        </Secured>
      );
    }
    return (
      <Secured
        allowForRoles={[UserRole.NELEGISLATIVNA_ORGANIZACIA, UserRole.VEREJNOST, UserRole.PRIPOMIENKOVATEL,
          UserRole.VYHODNOCOVATEL]}
        additionalCheck={processIsInMpk(legislationProcess) && pripomienkaDetail.typ === TypPripomienky.HROMADNA}
      >
        <LoaderButton
          onClick={onAddPodporovatelButtonClick}
          loading={addStatus.loading}
          className="add-support-button"
          type="button"
          size="large"
        >Podporiť pripomienku
        </LoaderButton>
      </Secured>
    );
  };

  return (
    <div className="detail-action-buttons">
      <Secured
        allowForRoles={[UserRole.VYHODNOCOVATEL]}
        additionalCheck={processInCorrectStates(legislationProcess)}
      >
        <Link
          className="vyhodnotit-button"
          type="button-action"
          size="large"
          to={getVyhodnotUrl()}
        >Vyhodnotiť
        </Link>
      </Secured>
      <AssignMePripomienkaButton
        pripomienkaUuid={pripomienkaDetail.uuid}
        reloadPage={reloadPripomienkaDetail}
        reloadFilterData={reloadFilterData}
        legislationProcess={legislationProcess}
      />
      <Secured
        allowForRoles={[UserRole.SUPERADMIN]}
      >
        <Link
          className="edit-button-admin"
          type="button-warning"
          size="large"
          to={getEditAdminUrl()}
        >Upraviť (admin)
        </Link>
      </Secured>
      <Secured
        allowForRoles={[UserRole.PRIPOMIENKOVATEL, UserRole.NELEGISLATIVNA_ORGANIZACIA]}
        additionalCheck={canEdit(pouzivatel, legislationProcess, pripomienkaDetail)}
      >
        <Link
          className="edit-button"
          type="button-primary"
          size="large"
          to={getEditUrl()}
        >Upraviť
        </Link>
      </Secured>
      <RemovePripomienkaButton
        classType={classType}
        pripomienkaDetail={pripomienkaDetail}
      />
      <ExportPodporovatelovButton
        pocetPodporovatelov={pripomienkaDetail.pocetPodporovatelov}
        legislationProcess={legislationProcess}
        pripomienkaType={pripomienkaDetail.typ}
      />
      {podporaButton()}
    </div>
  );
};

export default ActionButtons;
